import { Home, BarChart3, Crown, Swords, Trophy, Users } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { trackUmamiEvent } from '@/helpers/analytics';

export const MobileBottomNavigation = () => {
  return (
    <MobileBottomNavigationContent />
  );
};

const MobileBottomNavigationContent = () => {
  const [pressedItem, setPressedItem] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const location = useLocation();

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/duels', icon: Swords, label: 'Duels' },
    { path: '/crowd-rating', icon: BarChart3, label: 'Crowd Power' },
    { path: '/captain', icon: Crown, label: 'Captain' },
    { path: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
    { path: '/leagues', icon: Users, label: 'Leagues' },
  ];

  // Auto-hide on scroll: hide needs a deliberate scroll down; show triggers on small upward deltas
  // (symmetric ±20px required a full 20px jump per event — rare on trackpad/touch, so bar stayed hidden)
  useEffect(() => {
    let lastY = window.scrollY;
    const hideAfterDownPx = 14;
    const showAfterUpPx = 5;

    const handleScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      lastY = y;

      if (y < 16) {
        setIsVisible(true);
        return;
      }
      if (delta > hideAfterDownPx) {
        setIsVisible(false);
        return;
      }
      if (delta < -showAfterUpPx) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = (path: string, label: string) => {
    trackUmamiEvent('nav_mobile_bottom_click', { label, path });
    setPressedItem(path);

    // If clicking on already active path, scroll to top
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Haptic feedback if supported - 10ms light buzz
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }

    // Reset pressed state after animation
    setTimeout(() => setPressedItem(null), 150);
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 lg:hidden transition-transform duration-300 ease-out w-[92vw] max-w-[400px] h-16 rounded-[32px] bg-card/90 backdrop-blur-md shadow-lg touch-manipulation pb-[env(safe-area-inset-bottom)]"
      role="navigation"
      aria-label="Main mobile navigation"
      style={{
        transform: `translateX(-50%) ${isVisible ? 'translateY(0)' : 'translateY(100%)'}`,
      }}
    >
      <div className="flex h-full items-center justify-around px-4">
        {navItems.map(({ path, icon: Icon, label }) => {
          const active = isActive(path);
          const pressed = pressedItem === path;

          return (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className="flex flex-col items-center justify-center gap-0.5 transition-all duration-200 select-none touch-manipulation relative"
              onClick={() => handleClick(path, label)}
              onTouchStart={() => setPressedItem(path)}
              onTouchEnd={() => setTimeout(() => setPressedItem(null), 100)}
              onTouchCancel={() => setPressedItem(null)}
              aria-label={`Navigate to ${label}`}
              role="button"
              tabIndex={0}
            >
              <div
                className={`flex items-center justify-center transition-all duration-200 ${active
                  ? 'w-10 h-10 bg-primary rounded-full'
                  : 'w-6 h-6'
                  } ${pressed ? 'scale-95' : ''}`}
              >
                <Icon
                  className={`transition-colors ${active ? 'h-5 w-5 text-primary-foreground' : 'h-5 w-5 text-nav-inactive'
                    }`}
                />
              </div>
              <span
                className={`text-[9px] font-semibold leading-tight transition-all duration-200 max-[349px]:hidden ${active ? 'text-primary' : 'text-nav-inactive'
                  }`}
              >
                {label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};
